import React, { FC, useMemo } from 'react';
import { Box, Card, Typography } from '@mui/material';
import {
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  List as ListIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import MetricCard from './MetricCard';
import { formatNumber } from '../utils/formatters';
import { RangePreset } from '../types';

interface SalesMetricsCardsProps {
  activitySummary: any;
  rangePreset: RangePreset;
  activityLoading: boolean;
  onCallsClick?: () => void;
  onFollowUpsClick?: () => void;
  onBusinessSecuredClick?: () => void;
}

const SalesMetricsCards: FC<SalesMetricsCardsProps> = ({
  activitySummary,
  rangePreset,
  activityLoading,
  onCallsClick,
  onFollowUpsClick,
  onBusinessSecuredClick,
}) => {
  const metricCards = useMemo(() => {
    const summary = activitySummary;
    const calls = Number(summary?.noOfCalls ?? 0);
    const todayCalls = rangePreset === 'TODAY' ? Number(summary?.noOfCalls ?? 0) : 0;
    const followUps = summary?.tasks?.length ? formatNumber(summary.tasks.length) : '-';
    const business = Number(summary?.businessSecured ?? 0);
    const followUpsList = summary?.tasks || [];
    const followUpsTooltip =
      followUpsList.length > 0
        ? followUpsList
            .map(
              (task: any) => `${dayjs(task.createdAt).format('DD/MM/YY')} - ${task.status || task.description || '-'}`,
            )
            .join('\n')
        : 'No follow-ups';

    return [
      {
        title: 'No. of Calls',
        value: formatNumber(calls),
        description: '',
        icon: <PhoneIcon />,
        accentClassName: 'bg-emerald-100 text-emerald-700',
        onAction: summary?.salesCalls?.length ? onCallsClick : undefined,
        actionLabel: 'View details',
      },
      {
        title: 'Todays calls',
        value: formatNumber(todayCalls),
        description: '',
        icon: <CalendarTodayIcon />,
        accentClassName: 'bg-violet-100 text-violet-700',
      },
      {
        title: 'Follow-ups',
        value: followUps,
        description: '',
        icon: <ListIcon />,
        accentClassName: 'bg-amber-100 text-amber-700',
        tooltip: followUpsTooltip,
        onAction: followUpsList.length > 0 ? onFollowUpsClick : undefined,
        actionLabel: 'View details',
      },
      {
        title: 'Business Secured',
        value: formatNumber(business),
        description: '',
        icon: <AttachMoneyIcon />,
        accentClassName: 'bg-rose-100 text-rose-700',
        onAction: summary?.bookingLineItems?.length ? onBusinessSecuredClick : undefined,
        actionLabel: 'View details',
        minHeight: 120,
      },
    ];
  }, [activitySummary, rangePreset, onCallsClick, onFollowUpsClick, onBusinessSecuredClick]);

  return (
    <Card
      sx={{ px: 3, pt: 3, pb: 2, borderRadius: 2, backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
        Sales Activity
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {metricCards.map((metric, index) => (
          <Box
            key={metric.title}
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' },
              minWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' },
            }}>
            <MetricCard {...metric} loading={activityLoading && index === 0 && !activitySummary} />
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export default SalesMetricsCards;
